import React, {useState, useEffect} from 'react'
import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import {HeaderCell} from '../../../components/tables'
import {checkEvaluationUrl} from '../../../services/evaluation'
import type {EvaluationCheck} from '../../../services/types'
import type {EvaluationStageProps} from './types'

const modalStyle = {
  mt: 10,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
  maxHeight: 'calc(100% - 80px)', // Subtract some pixels for margin
}

const ListErrors = ({num_tasks, errors}: EvaluationCheck) => {
  if (errors.length === 0) {
    return <Typography>All {num_tasks} task are correct</Typography>
  }
  return (
    <Stack>
      <Typography>{errors.length} tasks with issues:</Typography>
      <TableContainer component={Paper}>
        <Table aria-label="Tasks with issues">
          <TableHead>
            <TableRow>
              <HeaderCell sx={{width: 20}}>Row</HeaderCell>
              <HeaderCell align="center" sx={{pl: 0}}>
                Errors
              </HeaderCell>
              <HeaderCell align="center" sx={{pl: 0}}>
                Warnings
              </HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map(({task_num, errors, warnings}, i) => (
              <TableRow key={i} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                <TableCell component="th">{task_num + 1}</TableCell>
                <TableCell align="left">{errors?.join(', ')}</TableCell>
                <TableCell align="left">{warnings?.join(', ')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

const CSVChecker = ({evaluation, onChange, enableNext, setNextHandler}: EvaluationStageProps) => {
  const [results, setResults] = useState<EvaluationCheck>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    const checkUrl = async () => {
      if (!evaluation.csv_url) return null

      enableNext(false)
      try {
        const res = await checkEvaluationUrl(
          evaluation.csv_url,
          evaluation.default_task_type,
          evaluation.columns_mapping
        )
        setResults(res)
        setError(undefined)
        if (res.errors.length === 0) {
          enableNext(true)
        }
      } catch (e) {
        setResults(undefined)
        setError(`${e}`)
      }
    }
    enableNext(false)
    evaluation.csv_url && checkUrl()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation.csv_url, evaluation.default_task_type, evaluation.columns_mapping])

  return (
    <>
      {!error && !results && (
        <>
          <Stack spacing={4} justifyContent="center" alignItems="center">
            <Typography>Fetching {evaluation.csv_url}</Typography>
            <CircularProgress />
          </Stack>
        </>
      )}
      {error && (
        <Typography variant="h4" sx={{color: 'red'}}>
          Error: {error}
        </Typography>
      )}
      {results && (
        <>
          <Typography>Total number of parsed tasks: {results.num_tasks}</Typography>
          <Typography>
            Minimum questions to complete this evaluation: {results?.min_questions_to_complete}
          </Typography>
          <ListErrors {...results} />
        </>
      )}
    </>
  )
}

type CSVCheckerModalProps = EvaluationStageProps & {
  onCancel?: () => void
  show?: boolean
}
export const CSVCheckerModal = ({show, evaluation, onChange, onCancel}: CSVCheckerModalProps) => (
  <Modal open={show || false} onClose={onCancel}>
    <Container sx={{flex: 1, ...modalStyle}}>
      {/* <CSVChecker evaluation={evaluation} onChange={onChange} /> */}
    </Container>
  </Modal>
)

export default CSVChecker
